/**
 * Words that describe a product rather than name its drug. Salts are only the
 * ones that trail a drug name; `potassium`/`sodium` stay, as they lead some.
 */
const NON_INGREDIENT_WORDS = new Set([
    'tablet',
    'tablets',
    'tab',
    'tabs',
    'capsule',
    'capsules',
    'cap',
    'caps',
    'oral',
    'chewable',
    'film',
    'coated',
    'extended',
    'delayed',
    'release',
    'solution',
    'suspension',
    'hcl',
    'hydrochloride',
    'mcg',
    'daily'
]);

const toIngredientWords = (name: string | null | undefined) =>
    (name ?? '')
        .toLowerCase()
        .split(/[^a-z]+/)
        .filter((word) => word.length > 2 && !NON_INGREDIENT_WORDS.has(word));

type HeroMedicationName = { name: string | null; name_short?: string | null };

/**
 * DrChrono sends RxNorm/NDC and Hero sends GPI, so there's no shared code to
 * join on — only the drug name. Two names match when they lead with the same
 * drug word: `metformin HCl 1000 mg tablet` ↔ `Metformin`. Only the leading word
 * is compared because a trailing one can't be told apart as salt or second
 * ingredient (`atorvastatin calcium` vs `sacubitril-valsartan`), so a combination
 * still matches its first single ingredient. A brand name with no generic in it
 * (`Eliquis` vs `apixaban`) can't match.
 */
export const findHeroMedication = <T extends HeroMedicationName>(
    drChronoName: string | null,
    heroMedications: T[]
) => {
    const [drug] = toIngredientWords(drChronoName);

    if (!drug) {
        return undefined;
    }

    return heroMedications.find((heroMedication) =>
        [heroMedication.name, heroMedication.name_short].some(
            (heroName) => toIngredientWords(heroName)[0] === drug
        )
    );
};
